<script setup lang="ts">
import type { Component } from 'vue';
import { CheckIcon, ChevronDown } from 'lucide-vue-next';
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { FilterOption } from './types';

withDefaults(
  defineProps<{
    label: string;
    triggerLabel: string;
    options: FilterOption[];
    modelValue: string;
    icon?: Component;
    align?: 'start' | 'center' | 'end';
    width?: string;
    testid?: string;
  }>(),
  { align: 'end', width: 'w-56' }
);

const emit = defineEmits<{ select: [value: string] }>();
</script>

<template>
  <Popover>
    <PopoverTrigger as-child>
      <button class="filter-trigger" :data-testid="testid">
        <component :is="icon" v-if="icon" class="h-3.5 w-3.5" />
        <span>{{ triggerLabel }}</span>
        <ChevronDown class="h-3.5 w-3.5 text-gray-400" />
      </button>
    </PopoverTrigger>
    <PopoverContent :align="align" :class="['p-1.5', width]" :side-offset="8">
      <div class="space-y-1">
        <div class="px-2 pb-1 text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500">{{ label }}</div>
        <PopoverClose
          v-for="option in options"
          :key="option.value"
          class="filter-row"
          :class="{ 'filter-row-active': modelValue === option.value }"
          @click="emit('select', option.value)"
        >
          <span class="min-w-0 truncate">{{ option.label }}</span>
          <CheckIcon v-if="modelValue === option.value" class="h-3.5 w-3.5 shrink-0" />
        </PopoverClose>
      </div>
    </PopoverContent>
  </Popover>
</template>

<style scoped>
@reference "tailwindcss";

.filter-trigger {
  @apply inline-flex items-center gap-1.5 rounded-md text-sm font-medium text-gray-500 transition-colors hover:text-gray-900;
}

.filter-row {
  @apply flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900;
}

.filter-row-active {
  @apply bg-gray-100 font-medium text-gray-900;
}
</style>
